<template>
  <div class="relative flex-shrink-0" :class="{ 'opacity-75': disabled }">
    <div class="relative" :class="disabled ? 'cursor-not-allowed' : ''">
      <div
        class="relative h-10 w-full pl-4 pr-12 flex items-center gap-2 border rounded cursor-pointer overflow-hidden"
        :class="[
          focus ? 'border-light-purple' : 'border-grey-200',
          disabled ? 'pointer-events-none bg-grey-100' : 'bg-pure-white'
        ]"
        @click.prevent="focus = true"
      >
        <div v-if="!input.label || !startDateInput.model">
          <BlTextBody2 :text="placeholder" class="text-slate-600 truncate" />
        </div>
        <div v-else>
          <BlTextBody2 :text="getLabel" class="text-slate-1000 truncate" />
        </div>
        <div
          class="absolute right-0 w-9 h-10 py-2.5 px-2 bg-pure-white"
        >
          <BlIcon
            icon="chevron-down"
            class="w-5 h-5 transition-all duration-300"
            :style="{ rotate: focus ? '180deg' : '' }"
          />
        </div>
      </div>
      <div
        :data-id="id"
        v-if="!disabled && focus"
        class="absolute right-0 mt-2 flex flex-col gap-2 flex-shrink-0 z-50 p-2 bg-pure-white rounded border border-grey-200 shadow-md"
      >
        <div
          :data-id="id"
          class="px-4 py-2 flex items-center justify-between border-b border-slate-400"
        >
          <BlTextSubtitle2 :data-id="id" :text="label" />
          <div :data-id="id" class="cursor-pointer" @click.prevent="onClearAll">
            <BlTextSubtitle2
              :data-id="id"
              class="text-light-purple hover:text-dark-purple-200"
              text="Clear all"
            />
          </div>
        </div>
        <div :data-id="id" class="px-4">
          <div
            v-for="opt in options"
            :key="opt.label"
            :data-id="id"
            class="py-2 flex items-center gap-2 truncate cursor-pointer"
            :title="opt.label"
            @click.prevent="onSelect(opt)"
          >
            <BlInputRadio
              :data-id="id"
              :name="opt.label"
              :checked="opt.label === input.label"
            />
            <BlTextBody2 :data-id="id" :text="opt.label" />
          </div>
        </div>
        <div :data-id="id" class="flex items-center gap-1">
          <BlInputDate
            :data-id="id"
            :input="startDateInput"
            @changed="inputHandler"
            disabledManualInput
          />
          <div :data-id="id">-</div>
          <BlInputDate
            :data-id="id"
            :input="endDateInput"
            @changed="inputHandler"
            disabledManualInput
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import BlTextSubtitle2 from "./BlTextSubtitle2.vue";
import BlTextBody2 from "./BlTextBody2.vue";
import BlIcon from "./BlIcon.vue";
import BlInputRadio from "./BlInputRadio.vue";
import BlInputDate from "./BlInputDate.vue";

export default {
  components: {
    BlTextSubtitle2,
    BlTextBody2,
    BlIcon,
    BlInputRadio,
    BlInputDate
  },
  name: "BlSingelFilterDate",
  props: {
    disabled: { type: Boolean },
    label: { type: String, default: "Date" },
    placeholder: { type: String, default: "By date" },
    value: { type: Object, default: () => ({ label: "", start: "", end: "" }) }
    // @change(value)
  },
  data() {
    return {
      input: { label: "" },
      startDateInput: { model: "", disabled: true, id: "" },
      endDateInput: { model: "", disabled: true, id: "" },
      focus: false
    };
  },
  computed: {
    id() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-single-filter-date-${combination}`;
    },
    getLabel() {
      const start = moment(this.startDateInput.model).format("DD/MM/YYYY");
      const end = this.endDateInput.model
        ? moment(this.endDateInput.model).format("DD/MM/YYYY")
        : "...";
      return `${start} - ${end}`;
    },
    options() {
      const week = {
        label: "Last 7 days",
        start: moment().add(-6, "days").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD")
      };
      const month = {
        label: "Last 30 days",
        start: moment().add(-29, "days").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD")
      };
      const year = {
        label: "Last 365 days",
        start: moment().add(-364, "days").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD")
      };
      const custom = {
        label: "Custom",
        start: moment().format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD")
      };
      return [week, month, year, custom];
    }
  },
  watch: {
    focus(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      } else {
        this.$emit("change", {
          label: this.input.label,
          start: this.startDateInput.model,
          end: this.endDateInput.model
        });
      }
    },
    value(val) {
      this.generateValue(val);
    }
  },
  mounted() {
    this.generateValue(this.value);
  },
  methods: {
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    generateValue(val) {
      this.input.label = val.label;
      this.startDateInput.model = val.start;
      this.endDateInput.model = val.end;
      this.startDateInput.id = this.id;
      this.endDateInput.id = this.id;

      if (val.label === "Custom") {
        this.startDateInput.disabled = false;
        this.endDateInput.disabled = false;
      } else {
        this.startDateInput.disabled = true;
        this.endDateInput.disabled = true;
      }
    },
    onClearAll() {
      this.input.label = "";
      this.startDateInput.model = "";
      this.endDateInput.model = "";
      this.startDateInput.disabled = true;
      this.endDateInput.disabled = true;
    },
    onSelect(opt) {
      this.input.label = opt.label;
      this.startDateInput.model = opt.start;
      this.endDateInput.model = opt.end;

      if (opt.label === "Custom") {
        this.startDateInput.disabled = false;
        this.endDateInput.disabled = false;
      } else {
        this.startDateInput.disabled = true;
        this.endDateInput.disabled = true;
      }
    },
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.id) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.focus = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/BlSingleFilterDate.vue